import { Controller, type Control } from 'react-hook-form';
import { Alert } from '@shared/components';
import { INVOICING_DEALER_GATED_NOTE, InvoicingDealerField } from './InvoicingDealerField';
import { CANDIDATES_LOAD_FAILED_REASON, NO_INVOICING_DEALER_REASON } from '../utils/invoiceSubmitGates';
import type { InvoiceSubmitValues } from '../schemas/invoiceSubmit.schema';
import type { InvoicingCandidate } from '../types/agreements.types';

export const INVOICING_DEALER_HINT =
    'The dealer raising this invoice. If the customer pays the dealer directly, this is the dealer that took the payment.';
export const INVOICING_DEALER_LOCKED_NOTE =
    'Locked: a payment has been recorded against this dealer. To use a different dealer, start a new invoice.';

export interface InvoicingDealerSectionProps {
    control: Control<InvoiceSubmitValues>;
    candidates: InvoicingCandidate[];
    candidatesLoading: boolean;
    candidatesError: boolean;
    candidatesGated: boolean;
    /** Disabled with the lock note: a top-up was recorded against this dealer. */
    locked: boolean;
    /** Disabled with no note: a top-up request is in flight. */
    busy: boolean;
}

function fieldNote(locked: boolean, gated: boolean): string | null {
    if (locked) return INVOICING_DEALER_LOCKED_NOTE;
    return gated ? INVOICING_DEALER_GATED_NOTE : null;
}

/** Rendered inside `PermissionGuard{invoice.add}`. */
export function InvoicingDealerSection({
    control,
    candidates,
    candidatesLoading,
    candidatesError,
    candidatesGated,
    locked,
    busy,
}: InvoicingDealerSectionProps) {
    const noInvoicingDealer =
        !candidatesGated && !candidatesLoading && !candidatesError && candidates.length === 0;
    const loadFailed = candidatesError && candidates.length === 0;

    return (
        <section aria-labelledby="invoicing-dealer-heading" className="space-y-3">
            <h3 id="invoicing-dealer-heading" className="text-sm font-semibold text-mp-foreground">
                Dealer
            </h3>
            <p className="text-sm text-mp-muted">{INVOICING_DEALER_HINT}</p>

            {loadFailed ? (
                <Alert variant="error">{CANDIDATES_LOAD_FAILED_REASON}</Alert>
            ) : noInvoicingDealer ? (
                <Alert variant="info" role="status">
                    {NO_INVOICING_DEALER_REASON}
                </Alert>
            ) : (
                <Controller
                    name="invoicingDealerId"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <InvoicingDealerField<InvoicingCandidate>
                            id="invoicing-dealer"
                            options={candidates}
                            value={candidates.find((candidate) => candidate.id === field.value) ?? null}
                            onChange={(candidate) => field.onChange(candidate?.id ?? '')}
                            getOptionValue={(candidate) => candidate.id}
                            getOptionLabel={(candidate) => candidate.name}
                            loading={candidatesLoading}
                            disabled={candidatesLoading || locked || busy}
                            emptyLabel={candidatesGated ? INVOICING_DEALER_GATED_NOTE : NO_INVOICING_DEALER_REASON}
                            error={error?.message}
                            note={fieldNote(locked, candidatesGated)}
                        />
                    )}
                />
            )}
        </section>
    );
}
